import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AgentChat } from './AgentChat';
import { apiService } from '../services/api';
import { getOrCreateAnonymousUserId } from '../utils/anonymousUserId';
import { Loader2 } from 'lucide-react';
import type { Agent } from '../types';

export const PublicAgentChat: React.FC = () => {
  const { tenantSlug, agentNameSlug } = useParams<{ tenantSlug: string; agentNameSlug: string }>();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anonymousAgentSid, setAnonymousAgentSid] = useState<string | undefined>(undefined);

  const effectiveTenantSlug = tenantSlug || import.meta.env.VITE_TENANT_SLUG || 'default';

  useEffect(() => {
    const loadPublicAgent = async () => {
      if (!agentNameSlug) {
        setError('Agent name slug is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch public agent
        const publicAgent = await apiService.getPublicAgent(effectiveTenantSlug, agentNameSlug);

        // Transform agent data to match Agent type
        const transformedAgent: Agent = {
          id: publicAgent.id,
          name: publicAgent.name,
          icon: publicAgent.icon,
          color: publicAgent.color,
          isPublic: publicAgent.isPublic,
          translations: publicAgent.translations,
          languages: publicAgent.languages,
          config: {
            VITE_CHAT_API_URL: publicAgent.chatApiUrl || '',
            VITE_CHAT_TITLE: publicAgent.name,
            VITE_AGENT_SID: publicAgent.agentSid || '',
            VITE_AGENT_SID_SOURCE: (publicAgent.agentSidSource || 'user_email') as 'oidc_user_id' | 'user_id' | 'user_email' | 'custom',
            VITE_CHAT_USER_ID: publicAgent.chatUserId || undefined,
            VITE_CUSTOM_FAVICON: publicAgent.customFavicon || undefined,
            VITE_STREAMING_ENABLED: publicAgent.streamingEnabled || undefined,
            VITE_ENABLE_PERSISTENCE: publicAgent.enablePersistence ?? undefined,
            VITE_ENABLE_PREEMPTIVE_END_CHAT_BUTTON: publicAgent.enablePreemptiveEndChatButton ?? undefined,
            VITE_ENABLE_CLEAR_CHAT_BUTTON: publicAgent.enableClearChatButton ?? undefined,
            VITE_SHOW_ROUTE_STACK: publicAgent.showRouteStack ?? undefined,
            VITE_FETCH_FOR_WELCOME_MESSAGE: publicAgent.fetchForWelcomeMessage ?? undefined,

            // Notification configuration
            VITE_ALLOW_NOTIFICATIONS: publicAgent.allowNotifications ?? undefined,
            VITE_FETCH_FOR_NOTIFICATION: publicAgent.fetchForNotification ?? undefined,
            VITE_NOTIFICATION_POOLING_FREQUENCY: publicAgent.notificationPoolingFrequency ?? undefined,

            // File upload configuration (chat input attachments)
            VITE_ALLOW_FILE_UPLOAD: publicAgent.allowFileUpload ?? undefined,
            VITE_ALLOW_IMAGE_PASTE: publicAgent.allowImagePaste ?? undefined,
            VITE_ALLOWED_FILE_TYPES: publicAgent.allowedFileTypes || undefined,
            VITE_MAX_FILE_SIZE: publicAgent.maxFileSize ?? undefined,
            VITE_MAX_FILES: publicAgent.maxFiles ?? undefined,

            // Theme configuration
            // Light theme
            VITE_THEME_PRIMARY: publicAgent.themePrimary || undefined,
            VITE_THEME_SECONDARY: publicAgent.themeSecondary || undefined,
            VITE_THEME_BACKGROUND: publicAgent.themeBackground || undefined,
            VITE_THEME_FOREGROUND: publicAgent.themeForeground || undefined,
            VITE_THEME_CARD: publicAgent.themeCard || undefined,
            VITE_THEME_CARD_FOREGROUND: publicAgent.themeCardForeground || undefined,
            VITE_THEME_PRIMARY_FOREGROUND: publicAgent.themePrimaryForeground || undefined,
            VITE_THEME_SECONDARY_FOREGROUND: publicAgent.themeSecondaryForeground || undefined,
            VITE_THEME_MUTED: publicAgent.themeMuted || undefined,
            VITE_THEME_MUTED_FOREGROUND: publicAgent.themeMutedForeground || undefined,
            VITE_THEME_BORDER: publicAgent.themeBorder || undefined,
            VITE_THEME_INPUT: publicAgent.themeInput || undefined,

            // Dark theme
            VITE_THEME_DARK_PRIMARY: publicAgent.themeDarkPrimary || undefined,
            VITE_THEME_DARK_SECONDARY: publicAgent.themeDarkSecondary || undefined,
            VITE_THEME_DARK_BACKGROUND: publicAgent.themeDarkBackground || undefined,
            VITE_THEME_DARK_FOREGROUND: publicAgent.themeDarkForeground || undefined,
            VITE_THEME_DARK_CARD: publicAgent.themeDarkCard || undefined,
            VITE_THEME_DARK_CARD_FOREGROUND: publicAgent.themeDarkCardForeground || undefined,
            VITE_THEME_DARK_PRIMARY_FOREGROUND: publicAgent.themeDarkPrimaryForeground || undefined,
            VITE_THEME_DARK_SECONDARY_FOREGROUND: publicAgent.themeDarkSecondaryForeground || undefined,
            VITE_THEME_DARK_MUTED: publicAgent.themeDarkMuted || undefined,
            VITE_THEME_DARK_MUTED_FOREGROUND: publicAgent.themeDarkMutedForeground || undefined,
            VITE_THEME_DARK_BORDER: publicAgent.themeDarkBorder || undefined,
            VITE_THEME_DARK_INPUT: publicAgent.themeDarkInput || undefined,

            // Feedback configuration
            VITE_FEEDBACK_ENABLED: publicAgent.feedbackEnabled || undefined,
            VITE_FEEDBACK_THUMBS_UP_URL: publicAgent.feedbackPositiveUrl || undefined,
            VITE_FEEDBACK_THUMBS_DOWN_URL: publicAgent.feedbackNegativeUrl || undefined,
            VITE_FEEDBACK_MIN_CHARS: publicAgent.feedbackMinChars ?? 20,
            VITE_RECAPTCHA_ENABLED: publicAgent.recaptchaEnabled ?? undefined,
            VITE_RECAPTCHA_SITE_KEY: publicAgent.recaptchaSiteKey || undefined,
            VITE_RECAPTCHA_VERSION: (publicAgent.recaptchaVersion as 'v2' | 'v3') || undefined,

            // Footnote configuration
            VITE_FOOTNOTE_ENABLED: publicAgent.footnoteEnabled || undefined,
            VITE_AGENT_MESSAGE_FOOTNOTE: publicAgent.agentMessageFootnote || undefined,
            VITE_FOOTNOTE_TITLE: publicAgent.footnoteTitle || undefined,
            VITE_FOOTNOTE_COLLAPSIBLE: publicAgent.footnoteCollapsible || undefined,
            VITE_FOOTNOTE_SHOW_TIMING: (publicAgent.footnoteShowTiming as 'immediately' | 'after_response' | 'let_agent_decide') || undefined,
          },
        };

        setAgent(transformedAgent);

        // Initialize anonymous agent_sid
        const enablePersistence = transformedAgent.config.VITE_ENABLE_PERSISTENCE ?? false;
        const sid = await getOrCreateAnonymousUserId(transformedAgent.id, enablePersistence);
        setAnonymousAgentSid(sid);
      } catch (err: any) {
        console.error('Failed to load public agent:', err);
        setError(err.message || 'Failed to load public agent');
      } finally {
        setLoading(false);
      }
    };

    loadPublicAgent();
  }, [agentNameSlug, effectiveTenantSlug]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading agent...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">Agent Not Found</h2>
          <p className="text-muted-foreground">
            {error || 'The requested public agent could not be found.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col" style={{ height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>
      <AgentChat
        isPublicMode={true}
        publicAgent={agent}
        publicAgentSid={anonymousAgentSid}
      />
    </div>
  );
};
